import { isDefined } from "@/utils/function-utils";
import { prettyFormatDurationSeconds } from "@/utils/date-time";

export type WindowedDuration = "infinity" | "all" | number;

export const WINDOWED_PERIOD_REGEX = "__(all|infinity|neg[0-9]+|-[0-9]+|[0-9]+)__(@\\d+)?";

export const windowPeriodAtEndRegex = /__(all|infinity|neg[0-9]+|-[0-9]+|[0-9]+)__(@\d+)?$/;
export const windowPeriodRegex = /__(all|infinity|neg[0-9]+|-[0-9]+|[0-9]+)__(@\d+)?/;
const WINDOWED_PERIOD_ENDING_REQUIREMENT_DEFAULT = true;

const getWindowPeriodRegex = (requireAtEnd: boolean) =>
  requireAtEnd ? windowPeriodAtEndRegex : windowPeriodRegex;

export const isWindowedChildName = (
  featureName: string,
  requireAtEnd = WINDOWED_PERIOD_ENDING_REQUIREMENT_DEFAULT,
): boolean => getWindowPeriodRegex(requireAtEnd).test(featureName);

export const getWindowedChildDuration = (
  featureName: string,
  requireAtEnd = WINDOWED_PERIOD_ENDING_REQUIREMENT_DEFAULT,
): WindowedDuration | undefined => {
  const group = getWindowPeriodRegex(requireAtEnd).exec(featureName);
  if (group == null || group[1] == null) {
    return undefined;
  }
  if (group[1] === "infinity" || group[1] === "all") return group[1];
  if (group[1].startsWith("neg")) return -parseFloat(group[1].slice(3));
  return parseFloat(group[1]);
};

/**
 * Transforms a windowed fqn for better readability
 * By making the __windowed_period__ into a (period) suffix
 *
 * Note that features and resolvers both have fqns, but the suffix appears at the end for features
 * So use the requireAtEnd argument to ensure that their feature names do not get mangled.
 *
 * @param featureName The raw fqn to be prettified
 * @param requireAtEnd Whether or not the windowed name is at the end of the fqn. True for features
 */
export const transformWindowedName = (
  featureName: string,
  requireAtEnd: boolean = WINDOWED_PERIOD_ENDING_REQUIREMENT_DEFAULT,
): string => {
  // efficient shortcut with `.test`
  if (!isWindowedChildName(featureName, requireAtEnd)) {
    return featureName;
  }

  const duration = getWindowedChildDuration(featureName, requireAtEnd);
  if (!isDefined(duration)) {
    return featureName;
  }

  const regex = getWindowPeriodRegex(requireAtEnd);
  const match = regex.exec(featureName);
  const versionSuffix = match?.[2] ?? "";
  const formattedDuration =
    typeof duration === "number" && duration < 0
      ? `-${prettyFormatDurationSeconds(-duration)}`
      : prettyFormatDurationSeconds(duration);
  return featureName.replace(regex, ` (${formattedDuration})${versionSuffix}`);
};

export const windowToNumber = (window: WindowedDuration): number => {
  if (window === "infinity" || window === "all") {
    return Infinity;
  }
  return window;
};

/**
 * Splits a windowed fqn into its readable name and windowed (period) suffix
 *
 * Note that features and resolvers both have fqns, but the suffix appears at the end for features
 * So use the requireAtEnd argument to ensure that their feature names do not get mangled.
 *
 * @param featureName The raw fqn to be prettified
 * @param requireAtEnd Whether or not the windowed name is at the end of the fqn. True for features
 */
export const splitWindowedName = (
  featureName: string,
  requireAtEnd: boolean = WINDOWED_PERIOD_ENDING_REQUIREMENT_DEFAULT,
): [string, WindowedDuration | undefined] => {
  const group = getWindowPeriodRegex(requireAtEnd).exec(featureName);
  if (group == null || group[1] == null) {
    return [featureName, undefined];
  }
  const versionSuffix = group[2] ?? "";
  const featureNameWithoutSuffix = featureName.replace(group[0], versionSuffix);
  let duration: WindowedDuration;
  if (group[1] === "infinity" || group[1] === "all") {
    duration = group[1];
  } else if (group[1].startsWith("neg")) {
    duration = -parseFloat(group[1].slice(3));
  } else {
    duration = parseFloat(group[1]);
  }
  return [featureNameWithoutSuffix, duration];
};
